// research — surowe namiary z researchu: szybkie dodawanie, edycja notatek, promocja do pipeline'u.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type KeyboardEvent } from 'react';
import type { Research } from '@delvero/contracts';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { relDate } from '../../lib/format';
import { orpc } from '../../lib/orpc';
import { useShell } from '../../shell';

interface QuickAddForm {
  company: string;
  phone: string;
  email: string;
  www: string;
  note: string;
}

const EMPTY_FORM: QuickAddForm = { company: '', phone: '', email: '', www: '', note: '' };

export function ResearchView() {
  const { activeAppId, openLead, setCallTarget } = useShell();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [form, setForm] = useState<QuickAddForm>(EMPTY_FORM);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Inline note edits live locally, keyed by research id, seeded lazily from the row's note.
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});

  const listQuery = useQuery({
    ...orpc.research.list.queryOptions({ input: { appId: activeAppId ?? '' } }),
    enabled: !!activeAppId,
  });

  const invalidateResearch = () => {
    void qc.invalidateQueries({ queryKey: orpc.research.key() });
  };
  const invalidatePromotion = () => {
    void qc.invalidateQueries({ queryKey: orpc.research.key() });
    void qc.invalidateQueries({ queryKey: orpc.leads.key() });
    void qc.invalidateQueries({ queryKey: orpc.dashboard.key() });
    void qc.invalidateQueries({ queryKey: orpc.costs.key() });
  };

  const createMutation = useMutation(
    orpc.research.create.mutationOptions({ onSuccess: invalidateResearch }),
  );
  const updateNoteMutation = useMutation(
    orpc.research.updateNote.mutationOptions({ onSuccess: invalidateResearch }),
  );
  const removeMutation = useMutation(
    orpc.research.remove.mutationOptions({ onSuccess: invalidateResearch }),
  );
  const promoteMutation = useMutation(
    orpc.research.promote.mutationOptions({ onSuccess: invalidatePromotion }),
  );

  const setField =
    (key: keyof QuickAddForm) =>
    (e: { target: { value: string } }) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const add = () => {
    if (!activeAppId || !form.company.trim()) return;
    createMutation.mutate(
      {
        appId: activeAppId,
        company: form.company.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        www: form.www.trim(),
        note: form.note.trim(),
      },
      { onSuccess: () => setForm(EMPTY_FORM) },
    );
  };

  const onAddKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') add();
  };

  const noteValue = (row: Research): string => noteDrafts[row.id] ?? row.note;

  const setNote = (id: string) => (e: { target: { value: string } }) => {
    const value = e.target.value;
    setNoteDrafts((prev) => ({ ...prev, [id]: value }));
  };

  const commitNote = (row: Research) => () => {
    const draft = noteDrafts[row.id];
    if (draft === undefined || draft === row.note) return;
    updateNoteMutation.mutate({ id: row.id, note: draft });
  };

  const promote = (row: Research, andCall: boolean) => {
    promoteMutation.mutate(
      { id: row.id, andCall },
      {
        onSuccess: (lead) => {
          if (andCall) {
            setCallTarget(lead.id);
            void navigate({ to: '/calling' });
          } else {
            openLead(lead.id);
          }
        },
      },
    );
  };

  if (!activeAppId || listQuery.isLoading) {
    return (
      <div className="view">
        <div className="card center-pad">
          <span className="spinner" /> Wczytywanie…
        </div>
      </div>
    );
  }

  const rows = (listQuery.data ?? [])
    .slice()
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

  return (
    <div className="view">
      <div className="filter-row">
        <span className="cell-sub">
          Surowe namiary z researchu — zanim trafią do pipeline&apos;u. Znalazłeś firmę z mailem
          contact@ albo telefonem? Wrzuć ją tutaj.
        </span>
        <div className="filter-spacer"></div>
        <button className="btn" onClick={() => setBulkOpen(true)}>
          <Icon name="pipeline" size={13} /> Wklej listę
        </button>
      </div>

      <div className="card table-card">
        <div className="quickadd">
          <input
            className="input"
            placeholder="Firma *"
            value={form.company}
            onChange={setField('company')}
            onKeyDown={onAddKeyDown}
          />
          <input
            className="input"
            placeholder="Telefon"
            value={form.phone}
            onChange={setField('phone')}
            onKeyDown={onAddKeyDown}
          />
          <input
            className="input"
            placeholder="contact@firma.pl"
            value={form.email}
            onChange={setField('email')}
            onKeyDown={onAddKeyDown}
          />
          <input
            className="input"
            placeholder="www"
            value={form.www}
            onChange={setField('www')}
            onKeyDown={onAddKeyDown}
          />
          <input
            className="input"
            placeholder="Notatka"
            value={form.note}
            onChange={setField('note')}
            onKeyDown={onAddKeyDown}
          />
          <button className="btn primary" onClick={add} disabled={!form.company.trim()}>
            <Icon name="plus" size={13} /> Dodaj
          </button>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: '24%' }}>Firma</th>
              <th style={{ width: '22%' }}>Kontakt</th>
              <th>Notatka</th>
              <th className="num">Dodano</th>
              <th className="num" style={{ width: '220px' }}>
                Akcje
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="no-click">
                <td>
                  <div className="cell-main">{r.company}</div>
                  {r.www ? <div className="cell-sub mono">{r.www}</div> : null}
                </td>
                <td>
                  {r.phone ? <div className="mono">{r.phone}</div> : null}
                  {r.email ? <div className="cell-sub">{r.email}</div> : null}
                  {!r.phone && !r.email ? <span className="cell-sub">—</span> : null}
                </td>
                <td>
                  <input
                    className="note-edit"
                    value={noteValue(r)}
                    placeholder="Dopisz notatkę…"
                    onChange={setNote(r.id)}
                    onBlur={commitNote(r)}
                  />
                </td>
                <td className="num cell-sub">{relDate(r.createdAt)}</td>
                <td className="num">
                  <div className="row-actions">
                    {r.phone ? (
                      <button className="btn slim2 primary" onClick={() => promote(r, true)}>
                        <Icon name="phone" size={12} /> Zadzwoń
                      </button>
                    ) : null}
                    <button className="btn slim2" onClick={() => promote(r, false)}>
                      Do pipeline <Icon name="arrow" size={12} />
                    </button>
                    <button
                      className="icon-btn del"
                      onClick={() => removeMutation.mutate({ id: r.id })}
                      title="Usuń"
                    >
                      <Icon name="x" size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-cell">
                  Pusto — dodaj pierwszy namiar powyżej albo wklej listę z researchu.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {bulkOpen ? (
        <BulkAddModal appId={activeAppId} onClose={() => setBulkOpen(false)} />
      ) : null}
    </div>
  );
}

interface ParsedRow {
  company: string;
  phone: string;
  email: string;
  www: string;
}

function parseBulk(text: string): ParsedRow[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line): ParsedRow => {
      const parts = line
        .split(/[;\t]/)
        .map((x) => x.trim())
        .filter(Boolean);
      const email = parts.find((p) => p.includes('@')) ?? '';
      const phone = parts.find((p) => /^\+?[\d\s\-()]{7,}$/.test(p)) ?? '';
      const www =
        parts.find(
          (p) => p !== email && /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(p) && !p.includes('@'),
        ) ?? '';
      return { company: parts[0] ?? '', phone, email, www };
    })
    .filter((p) => p.company);
}

function BulkAddModal({ appId, onClose }: { appId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const parsed = parseBulk(text);

  const bulkCreateMutation = useMutation(
    orpc.research.bulkCreate.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: orpc.research.key() });
        onClose();
      },
    }),
  );

  const save = () => {
    if (parsed.length === 0) return;
    bulkCreateMutation.mutate({
      appId,
      items: parsed.map((p) => ({ company: p.company, phone: p.phone, email: p.email, www: p.www })),
    });
  };

  return (
    <Modal
      title="Wklej listę namiarów"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Anuluj
          </button>
          <button
            className="btn primary"
            onClick={save}
            disabled={parsed.length === 0 || bulkCreateMutation.isPending}
          >
            Dodaj {parsed.length > 0 ? parsed.length : ''} {parsed.length === 1 ? 'firmę' : 'firm'}
          </button>
        </>
      }
    >
      <div className="cell-sub" style={{ marginBottom: 8 }}>
        Jedna firma na linię, pola oddzielone średnikiem lub tabem:
        <br />
        <span className="mono">Firma; +48 600 100 200; contact@firma.pl</span> — telefon i e-mail
        rozpoznam automatycznie.
      </div>
      <textarea
        className="input mono"
        rows={7}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={'Pirotex Serwis; +48 89 533 20 41; kontakt@pirotex.pl\nGaśTech Białystok; biuro@gastech.pl'}
      ></textarea>
    </Modal>
  );
}
